import { FC } from 'react';
import { Modal, SelectInput, TextField } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Stack } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { usePredefinedToasts } from 'utils/Toast';
import { routerHistory } from 'utils/router-history';
import { ROUTES } from 'config';
import {
  benefitCategories,
  BenefitCategory,
  useCreateBenefit,
} from 'store/benefits';

interface Values {
  name: string;
  category: BenefitCategory;
}

const INITIAL_VALUES: Values = {
  name: '',
  category: benefitCategories[0],
};

interface Props extends ModalProps {
  templateId?: string;
  initialValues?: Values;
}

export const CreateBenefitModal: FC<Props> = ({
  templateId,
  initialValues,
  closeModal,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { control, handleSubmit } = useForm<Values>({
    defaultValues: initialValues ?? INITIAL_VALUES,
  });

  const { mutate: createBenefit, isLoading: isCreating } = useCreateBenefit();

  const onSubmit: SubmitHandler<Values> = (values) => {
    createBenefit(
      {
        name: values.name,
        type: values.category,
        templateId,
      },
      {
        onSuccess: ({ data }) => {
          closeModal();
          predefinedToasts.success.added();
          routerHistory.push({
            pathname: ROUTES.benefits.details.replace(':id', data.id),
            search: 'guided=true',
          });
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('buttons.new_benefit')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.close'),
          },
          {
            variant: 'primary',
            label: t('common.create'),
            type: 'submit',
            disabled: isCreating,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="category"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <SelectInput
                label={t('benefits.benefitCategory')}
                name={name}
                value={value}
                onChange={onChange}
                options={benefitCategories.map((category) => ({
                  value: category,
                  label: t(`menu.${category}`),
                }))}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="name"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('common.systemName')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
